import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, MapPin, Pencil, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react'
import { LOT_MODES, type LotMode, type StageDisciplineInfo } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Input,
  Modal,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  GRADING_KEY,
  SHEET_VARIANT,
  useEvaluators,
  useGradingOverview,
  type GradingOverview,
  type OverviewSheet,
} from '../../lib/grading'
import { Progress } from './Progress'

type Lot = GradingOverview['lots'][number]
interface Slot {
  lot: Lot
  discipline: StageDisciplineInfo | null
  round: number
  sheet: OverviewSheet | null
}

/**
 * Écran commission d'une étape (cahier §9.5) : liste des candidats, anonymat,
 * lots, affectations des évaluateurs, suivi de saisie, anomalies, validation.
 */
export function CommissionStagePage() {
  const { stageId = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const query = useGradingOverview(stageId)
  const o = query.data
  const scope = o ? { type: 'competition' as const, id: o.competition.id } : undefined
  const canAssign = useCan('evaluators.assign', scope)
  const canValidate = useCan('notes.validate', scope)
  const [assign, setAssign] = useState<Slot | null>(null)
  const [lotEdit, setLotEdit] = useState<Lot | null>(null)
  const [confirm, setConfirm] = useState<null | 'anonymous' | 'lift' | { remove: OverviewSheet }>(
    null
  )
  const [lots, setLots] = useState({ mode: 'alphabetical' as LotMode, size: '30', prefix: 'Lot' })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GRADING_KEY })
  const mutate = useMutation({
    mutationFn: async (op: {
      kind: string
      body?: unknown
      url: string
      method: 'post' | 'put' | 'delete'
    }) => {
      if (op.method === 'delete') await session.api.delete(op.url)
      else if (op.method === 'put') await session.api.put(op.url, op.body ?? {})
      else await session.api.post(op.url, op.body ?? {})
      return op.kind
    },
    onSuccess: async (kind) => {
      toast.success(
        {
          population: 'Liste des candidats à jour',
          lots: 'Lots constitués',
          anonymity: 'Anonymat mis à jour',
          lift: 'Anonymat levé',
          validate: 'Relevé validé',
          remove: 'Affectation retirée',
        }[kind] ?? 'Enregistré'
      )
      setConfirm(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (query.isError) return <Alert tone="danger">{errorMessage(query.error)}</Alert>
  if (!o)
    return (
      <Card>
        <CardBody>Chargement…</CardBody>
      </Card>
    )

  const base = `/admin/stages/${stageId}`
  const hasSheets = o.sheets.length > 0
  const validated = o.sheets.filter((s) => s.status === 'validated').length
  const submitted = o.sheets.filter((s) => s.status === 'submitted').length
  const disciplines: (StageDisciplineInfo | null)[] = o.stage.graded ? o.disciplines : [null]
  const roundsFor = (lot: Lot, d: StageDisciplineInfo | null) => {
    if (!d) return [1]
    if (d.multiRule !== 'max_gap') return Array.from({ length: d.evaluators }, (_, i) => i + 1)
    const third =
      o.sheets.some((s) => s.lotId === lot.id && s.stageDisciplineId === d.id && s.round === 3) ||
      o.anomalies.some(
        (a) => a.kind === 'gap' && a.lotId === lot.id && a.stageDisciplineId === d.id
      )
    return third ? [1, 2, 3] : [1, 2]
  }
  const sheetFor = (lot: Lot, d: StageDisciplineInfo | null, round: number) =>
    o.sheets.find(
      (s) =>
        s.lotId === lot.id && (s.stageDisciplineId ?? null) === (d?.id ?? null) && s.round === round
    ) ?? null
  const canLift =
    canValidate &&
    o.stage.anonymous &&
    !o.stage.anonymityLiftedAt &&
    hasSheets &&
    validated === o.sheets.length

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/commissions" className="link">
            Commissions et notes
          </Link>
        }
        title={`${o.stage.label} — ${o.competition.title}`}
        description={
          <>
            <span className="mono">{o.competition.reference}</span> · étape {o.stage.position} ·{' '}
            {o.stage.statusLabel}
          </>
        }
        actions={
          canLift && (
            <Button variant="primary" icon={<Eye />} onClick={() => setConfirm('lift')}>
              Lever l’anonymat
            </Button>
          )
        }
      />

      <StatGrid>
        <Stat label="Candidats" value={o.population.total} />
        <Stat label="Lots" value={o.lots.length} />
        <Stat label="Relevés à valider" value={submitted} />
        <Stat label="Relevés validés" value={`${validated} / ${o.sheets.length}`} />
      </StatGrid>

      {o.anomalies.length > 0 && (
        <Alert tone="warning" title={`Points d’attention (${o.anomalies.length})`}>
          <ul className="sel-issues">
            {o.anomalies.slice(0, 12).map((a, i) => (
              <li key={i}>{a.message}</li>
            ))}
            {o.anomalies.length > 12 && <li>… et {o.anomalies.length - 12} autre(s).</li>}
          </ul>
        </Alert>
      )}

      {canAssign && (
        <Card>
          <CardHeader title="Préparation" />
          <CardBody>
            <div className="prep-grid">
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                <h3 className="sel-section-title">1. Liste des candidats</h3>
                <p className="muted" style={{ margin: 0 }}>
                  {o.stage.population === 'admissible'
                    ? 'Dossiers déclarés recevables (à relancer si de nouveaux dossiers le deviennent).'
                    : 'Admis de l’étape précédente : liste remplie automatiquement à l’approbation de ses résultats ; à relancer après une rectification.'}
                </p>
                <div>
                  <Button
                    icon={<Users />}
                    loading={mutate.isPending && mutate.variables?.kind === 'population'}
                    onClick={() =>
                      mutate.mutate({
                        kind: 'population',
                        url: `${base}/population`,
                        method: 'post',
                      })
                    }
                  >
                    {o.population.total ? 'Mettre à jour la liste' : 'Constituer la liste'}
                  </Button>
                </div>
              </div>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                <h3 className="sel-section-title">2. Anonymat</h3>
                <p className="muted" style={{ margin: 0 }}>
                  {o.stage.anonymous
                    ? o.stage.anonymityLiftedAt
                      ? 'Anonymat levé.'
                      : 'Actif : les évaluateurs ne voient qu’un code par candidat.'
                    : 'Inactif : les évaluateurs voient nom et matricule.'}
                </p>
                <div>
                  <Button
                    icon={o.stage.anonymous ? <EyeOff /> : <Eye />}
                    disabled={Boolean(o.stage.anonymityLiftedAt)}
                    onClick={() => setConfirm('anonymous')}
                  >
                    {o.stage.anonymous ? 'Désactiver l’anonymat' : 'Activer l’anonymat'}
                  </Button>
                </div>
              </div>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                <h3 className="sel-section-title">3. Lots</h3>
                <div
                  className="row"
                  style={{ gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}
                >
                  <Select
                    label="Répartition"
                    value={lots.mode}
                    disabled={hasSheets}
                    options={Object.entries(LOT_MODES).map(([value, label]) => ({ value, label }))}
                    onChange={(e) => setLots({ ...lots, mode: e.target.value as LotMode })}
                  />
                  <Input
                    label="Candidats par lot"
                    type="number"
                    min={1}
                    value={lots.size}
                    disabled={hasSheets}
                    onChange={(e) => setLots({ ...lots, size: e.target.value })}
                    style={{ maxWidth: '8rem' }}
                  />
                  <Input
                    label="Préfixe"
                    value={lots.prefix}
                    disabled={hasSheets}
                    onChange={(e) => setLots({ ...lots, prefix: e.target.value })}
                    style={{ maxWidth: '9rem' }}
                  />
                  <Button
                    disabled={hasSheets || !o.population.total || !(Number(lots.size) >= 1)}
                    loading={mutate.isPending && mutate.variables?.kind === 'lots'}
                    onClick={() =>
                      mutate.mutate({
                        kind: 'lots',
                        url: `${base}/lots`,
                        method: 'post',
                        body: { mode: lots.mode, size: Number(lots.size), prefix: lots.prefix },
                      })
                    }
                  >
                    {o.lots.length ? 'Refaire les lots' : 'Constituer les lots'}
                  </Button>
                </div>
                {hasSheets && (
                  <p className="muted" style={{ margin: 0 }}>
                    Évaluateurs affectés : les lots ne se refont plus (salle et centre restent
                    modifiables).
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {o.stage.status !== 'grading' && (
        <Alert tone="info">
          L’étape n’est pas en notation : les affectations et la saisie sont fermées.
        </Alert>
      )}

      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {o.lots.map((lot) => (
          <Card key={lot.id}>
            <CardHeader
              title={`${lot.label} · ${lot.count} candidat(s)`}
              actions={
                canAssign && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Pencil />}
                    onClick={() => setLotEdit(lot)}
                  >
                    Salle, centre
                  </Button>
                )
              }
            />
            {(lot.room || lot.center) && (
              <p className="muted lot-place">
                <MapPin aria-hidden="true" size={14} />{' '}
                {[lot.room, lot.center].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="tbl-wrap">
              <table className="tbl">
                <caption className="sr-only">Affectations du {lot.label}</caption>
                <thead>
                  <tr>
                    <th scope="col">Discipline</th>
                    <th scope="col">Correction</th>
                    <th scope="col">Évaluateur</th>
                    <th scope="col">Avancement</th>
                    <th scope="col">État</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {disciplines.flatMap((d) =>
                    roundsFor(lot, d).map((round) => {
                      const sheet = sheetFor(lot, d, round)
                      const slot: Slot = { lot, discipline: d, round, sheet }
                      return (
                        <tr key={`${d?.id ?? 'status'}-${round}`}>
                          <td>{d?.label ?? 'Statut de l’étape'}</td>
                          <td>{round === 3 ? '3 (écart)' : round}</td>
                          <td>
                            {sheet ? (
                              <span className="stack" style={{ gap: 0 }}>
                                <span>{sheet.evaluator.name}</span>
                                {sheet.commission && (
                                  <span className="muted">{sheet.commission}</span>
                                )}
                              </span>
                            ) : (
                              <span className="muted">Non affecté</span>
                            )}
                          </td>
                          <td>
                            {sheet && (
                              <Progress
                                done={sheet.progress.done}
                                total={sheet.progress.total}
                                label={`Avancement — ${lot.label}, correction ${round}`}
                              />
                            )}
                          </td>
                          <td>
                            {sheet && (
                              <Badge variant={SHEET_VARIANT[sheet.status]}>
                                {sheet.statusLabel}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <span
                              className="row"
                              style={{ gap: 4, flexWrap: 'nowrap', justifyContent: 'flex-end' }}
                            >
                              {!sheet && canAssign && o.stage.status === 'grading' && (
                                <Button
                                  size="sm"
                                  icon={<UserPlus />}
                                  onClick={() => setAssign(slot)}
                                >
                                  Affecter
                                </Button>
                              )}
                              {sheet && (
                                <Link
                                  className="btn btn-ghost btn-sm"
                                  to={`/notes/releves/${sheet.id}`}
                                >
                                  Ouvrir
                                </Link>
                              )}
                              {sheet?.status === 'submitted' && canValidate && (
                                <Button
                                  size="sm"
                                  variant="primary"
                                  icon={<ShieldCheck />}
                                  onClick={() =>
                                    mutate.mutate({
                                      kind: 'validate',
                                      url: `/admin/sheets/${sheet.id}/validate`,
                                      method: 'post',
                                    })
                                  }
                                >
                                  Valider
                                </Button>
                              )}
                              {sheet?.status === 'draft' &&
                                sheet.progress.done === 0 &&
                                canAssign && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    iconOnly
                                    icon={<Trash2 />}
                                    aria-label={`Retirer l’affectation de ${sheet.evaluator.name}`}
                                    onClick={() => setConfirm({ remove: sheet })}
                                  />
                                )}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>

      {assign && <AssignModal stageId={stageId} slot={assign} onClose={() => setAssign(null)} />}
      {lotEdit && <LotModal lot={lotEdit} onClose={() => setLotEdit(null)} />}
      <ConfirmModal
        open={confirm === 'anonymous'}
        onClose={() => setConfirm(null)}
        title={o.stage.anonymous ? 'Désactiver l’anonymat ?' : 'Activer l’anonymat ?'}
        message={
          o.stage.anonymous
            ? 'Les évaluateurs verront le nom et le matricule des candidats.'
            : 'Un code aléatoire est attribué à chaque candidat ; les évaluateurs ne verront que ce code. Possible tant qu’aucune note n’est saisie.'
        }
        confirmLabel="Confirmer"
        onConfirm={() =>
          mutate
            .mutateAsync({
              kind: 'anonymity',
              url: `${base}/anonymity`,
              method: 'put',
              body: { anonymous: !o.stage.anonymous },
            })
            .then(() => undefined)
        }
      />
      <ConfirmModal
        open={confirm === 'lift'}
        onClose={() => setConfirm(null)}
        title="Lever l’anonymat ?"
        message="Tous les relevés sont validés. Les noms deviennent visibles sur les relevés ; l’opération est journalisée."
        confirmLabel="Lever l’anonymat"
        reasonRequired
        reasonLabel="Motif"
        reasonMinLength={10}
        onConfirm={(reason) =>
          mutate
            .mutateAsync({
              kind: 'lift',
              url: `${base}/anonymity/lift`,
              method: 'post',
              body: { reason },
            })
            .then(() => undefined)
        }
      />
      <ConfirmModal
        open={typeof confirm === 'object' && confirm !== null}
        onClose={() => setConfirm(null)}
        tone="danger"
        title="Retirer cette affectation ?"
        message="Possible tant qu’aucune note n’a été saisie sur le relevé."
        confirmLabel="Retirer"
        onConfirm={() =>
          typeof confirm === 'object' && confirm
            ? mutate
                .mutateAsync({
                  kind: 'remove',
                  url: `/admin/sheets/${confirm.remove.id}`,
                  method: 'delete',
                })
                .then(() => undefined)
            : undefined
        }
      />
    </>
  )
}

function AssignModal({
  stageId,
  slot,
  onClose,
}: {
  stageId: string
  slot: Slot
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const evaluators = useEvaluators(stageId, true)
  const [form, setForm] = useState({ evaluatorId: '', commission: '' })
  const save = useMutation({
    mutationFn: () =>
      session.api.post(`/admin/stages/${stageId}/sheets`, {
        lotId: slot.lot.id,
        stageDisciplineId: slot.discipline?.id ?? null,
        round: slot.round,
        evaluatorId: form.evaluatorId,
        commission: form.commission.trim() || null,
      }),
    onSuccess: async () => {
      toast.success('Évaluateur affecté')
      await queryClient.invalidateQueries({ queryKey: GRADING_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)
  return (
    <Modal
      open
      onClose={onClose}
      title="Affecter un évaluateur"
      description={`${slot.lot.label} · ${slot.discipline?.label ?? 'Statut de l’étape'} · correction ${slot.round}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!form.evaluatorId}
            onClick={() => save.mutate()}
          >
            Affecter
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        <Select
          label="Évaluateur"
          required
          placeholder="— Choisir —"
          value={form.evaluatorId}
          error={errors.evaluatorId}
          hint="Comptes habilités à saisir des notes sur ce concours. Un correcteur ne corrige qu’une fois chaque copie."
          options={(evaluators.data ?? []).map((e) => ({
            value: e.id,
            label: `${e.name} — ${e.email}`,
          }))}
          onChange={(e) => setForm({ ...form, evaluatorId: e.target.value })}
        />
        <Input
          label="Commission"
          value={form.commission}
          hint="Facultatif (ex. « Commission 1 »)."
          onChange={(e) => setForm({ ...form, commission: e.target.value })}
        />
      </div>
    </Modal>
  )
}

function LotModal({ lot, onClose }: { lot: Lot; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    label: lot.label,
    room: lot.room ?? '',
    center: lot.center ?? '',
  })
  const save = useMutation({
    mutationFn: () =>
      session.api.patch(`/admin/lots/${lot.id}`, {
        label: form.label.trim(),
        room: form.room.trim() || null,
        center: form.center.trim() || null,
      }),
    onSuccess: async () => {
      toast.success('Lot enregistré')
      await queryClient.invalidateQueries({ queryKey: GRADING_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Lot ${lot.label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!form.label.trim()}
            onClick={() => save.mutate()}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        <Input
          label="Nom du lot"
          required
          value={form.label}
          error={errors.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <Input
          label="Salle"
          value={form.room}
          onChange={(e) => setForm({ ...form, room: e.target.value })}
        />
        <Input
          label="Centre"
          value={form.center}
          onChange={(e) => setForm({ ...form, center: e.target.value })}
        />
      </div>
    </Modal>
  )
}
